import { useCallback, useEffect, useRef, useState } from "react";
import { connectSocket } from "../../../socket";
import { refreshAccessTokenDetailed } from "../../../utils/auth";

const EVENTS = [
  // Ack for tse:join, carrying `resumed`: whether the server still held this
  // session or had to build a fresh one. It is the only signal separating a
  // recoverable network blip from a session the server has genuinely lost, so it
  // is what decides between reconnecting and locking the composer.
  "tse:joined",
  "tse:chunk",
  "tse:sources_preview",
  "tse:sources_enriched",
  "tse:source_replacement_started",
  "tse:source_replacement_complete",
  "tse:source_replacement_error",
  "tse:question",
  "tse:info",
  // What the agent is doing before it starts writing. Streaming only begins once
  // the model produces text, so retrieval and the first tool hop are otherwise silent.
  "tse:status",
  "tse:bugs",
  "tse:cases",
  "tse:done",
  "tse:error",
  "tse:node_visited",
  // Emitted when the agent run ends without asking anything back. tse:done
  // alone can't say this: inform_user is mid-turn, so it reports
  // turn_complete: false and would otherwise leave the composer locked.
  "tse:idle",
  // Session closed — renders an in-chat button, not a question card.
  "tse:feedback_prompt",
  // The server minted a session id for this client (see tse:start). The id is
  // NOT generated in the browser: it arrives here, and only then does the
  // session have an address, a Recents row and somewhere to put a turn.
  "tse:session_created",
  // The session belongs to someone else. Distinct from tse:error because it is
  // not a failure to recover from: the id is not ours and never will be, so the
  // UI leaves this session entirely rather than retrying or locking a composer
  // over a transcript it must not show.
  "tse:access_denied",
  // Idle timeout. Distinct from tse:error because the session is gone for good:
  // the server refuses to mint a replacement under this id, so the UI must lock
  // the composer rather than let the user type into nothing.
  "tse:session_expired",
  // Copilot runtime mode: "guided" | "fast" — drives Exit guided control.
  "tse:mode",
  // The session has moved past its first prompt, so the "Product identified"
  // banner can no longer be changed.
  "tse:product_locked",
  // The two answers to tse:change_product. ACCEPTED goes to the whole room,
  // after the old run has stopped and before the re-run starts — it is the
  // only thing allowed to wipe the wrong-product answer. REFUSED goes to the
  // asking socket; nothing was wiped, so it only clears the pending control.
  "tse:product_change_accepted",
  "tse:product_change_refused",
  // Structured Case Analysis panels (same UI as /case-analysis/:id).
  "tse:case_analysis",
  // Replayed turn history for a hydrated (resumed-from-storage) session.
  "tse:replay",
  // Cleared session — may carry a new session_id when resume mints a fresh id.
  "tse:cleared",
  // The engineer's OWN message, echoed by the server to every OTHER tab on
  // this session (the sending socket is skipped — it drew the bubble itself).
  // Turn output already fans out to the room; without this a second tab saw
  // the answer but never the message, and kept an answered question card up.
  "tse:user_message",
  "sentimentUpdate",
  // Live agent message during escalation — an agent has joined and sent a message
  "liveAgentMessage",
  // Server notification that this session has been escalated to a live agent
  "tse:escalated",
  // Ack for tse:bind_escalation: this socket is now the live agent on a
  // DIFFERENT session (the customer's), while its own copilot session stays
  // private. Carries the customer's id and username so the LHS can address
  // them without the dashboard guessing.
  "tse:escalation_bound",
  "tse:live_chat_ended",
  // The OTHER side of a live chat dropped, came back, or ran out of time.
  // { party: "agent" | "customer", state: "reconnecting" | "reconnected" |
  //   "lost", seconds_left, timeout_s } -> onLiveChatPeer
  "tse:live_chat_peer",
  // Agent joined the escalated session
  "agentJoined",
  // The TSE-path counterpart of agentJoined. sockets.py emits this one (not
  // agentJoined) for sessions owned by sockets_tse, so a client that listened
  // only for agentJoined never learned an agent had picked the session up.
  "tseAgentJoined",
  // Backend sends the resolved customer username when a TSE agent joins an escalated session
  "customerUsername",
  // V5 diagnosis panel (W7b / F2). Full panel snapshot after every DA turn, on
  // revive and on command results; ordered client-side by (diagnosis_id, seq).
  "tse:diag_state",
  // The small "switch to automatic" banner (confirm / dismiss commands).
  "tse:diagnosis_prompt",
  // Ack for every kind answer / command: applied, ignored (stale) or rejected.
  "tse:command_result",
  // The learning banner row (learned / teach / sent / off).
  "tse:learning_prompt",
  // V5 DA narration (tse_da_narration.schema.json): the DA turn's text, after the
  // card. pending -> start (sources) -> delta* -> end | abort.
  "tse:da_narration_pending",
  "tse:da_narration_start",
  "tse:da_narration_delta",
  "tse:da_narration_end",
  "tse:da_narration_abort",
];

// Maps each server event to the handler key the caller supplies, e.g.
// "tse:sources_preview" -> onSourcesPreview
const toHandlerKey = (evt) =>
  "on" +
  evt
    .replace(/^tse:/, "")
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join("");

/**
 * useTseSocket — owns the single Socket.IO connection for Dashboard5.
 * Joins the tse:* room on connect, fans server events out to caller-supplied
 * handlers (kept in a ref so the socket isn't torn down/rebuilt every render),
 * and exposes send/stop helpers.
 *
 * handlers: { onChunk, onSourcesPreview, onSourcesEnriched, onQuestion,
 *             onInfo, onBugs, onCases, onDone, onError, onNodeVisited }
 */
export default function useTseSocket(
  sessionId,
  handlers = {},
  loginUsername = "",
  aiAgentName = "",
  surface = "copilot",
) {
  const socketRef = useRef(null);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const loginUsernameRef = useRef(loginUsername);
  if (loginUsername) loginUsernameRef.current = loginUsername;
  const aiAgentNameRef = useRef(aiAgentName);
  if (aiAgentName) aiAgentNameRef.current = aiAgentName;
  // Which HUMAN is on this session — an enterprise customer or a support
  // engineer. The server prefers the role behind the socket's Cognito token and
  // only falls back to this when that role cannot be read, so it is a hint, not
  // a grant: the most a forged value achieves is escalating your own session.
  const surfaceRef = useRef(surface);
  if (surface) surfaceRef.current = surface;
  // The id every emit below targets, in a ref rather than read from the
  // `sessionId` argument directly, for two reasons that used to bite:
  //
  //   * The socket must NOT be rebuilt when the id changes. A blank chat has no
  //     id at all until the server mints one, and rebuilding the transport the
  //     moment it arrives would drop the connection in the gap between "session
  //     created" and the first turn's output — which is addressed to the room
  //     this socket just entered.
  //   * Emitters that close over a render value send to the PREVIOUS id for one
  //     render after the id changes. That is why the clear path had to reach
  //     around this hook and emit on the raw socket. adoptSession() moves the
  //     ref synchronously, so a caller can adopt an id and send on it in the
  //     same tick.
  const sessionIdRef = useRef(sessionId);
  if (sessionId && sessionIdRef.current !== sessionId) sessionIdRef.current = sessionId;
  // Set by the connect effect; lets a caller re-issue the join for the CURRENT
  // socket without tearing it down (see joinRef assignment below).
  const joinRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [socketInstance, setSocketInstance] = useState(null);

  useEffect(() => {
    const sock = connectSocket();
    socketRef.current = sock;
    setSocketInstance(sock);

    // No id yet means a blank chat that has not asked the server for one.
    // There is nothing to join — and joining an id we invented is exactly the
    // behaviour tse:start replaced.
    const emitJoin = () => {
      const id = sessionIdRef.current;
      if (!id) return;
      // The username the caller was rendered with, so the backend can resolve a
      // display name even when the DB lookup (resolve_app_user) fails or the
      // user is not in the users table — the backend uses it as a fallback and
      // never lets it override a verified identity.
      //
      // localStorage is deliberately NOT consulted. It used to be the second
      // fallback here, which put an editable value into the name the agent's
      // escalation queue displays; the prop is sourced from the authenticated
      // session, so the fallback bought nothing but the spoof.
      const storedUsername = loginUsernameRef.current || undefined;
      const resolvedAiName = String(aiAgentNameRef.current || "").trim();
      sock.emit("tse:join", {
        session_id: id,
        username: storedUsername,
        surface: surfaceRef.current || "copilot",
        ...(resolvedAiName && resolvedAiName !== "AI Agent"
          ? { ai_agent_name: resolvedAiName }
          : {}),
      });
    };
    // Exposed as join() below. Once shouldRejoin has refused a rejoin the socket
    // can be CONNECTED but unjoined, and `connect` does not fire again — so
    // unlocking on its own cannot recover the session. The caller needs a way to
    // ask for the join explicitly once it has unlocked.
    joinRef.current = emitJoin;

    const onConnect = () => {
      setIsConnected(true);
      // The transport is back. Callers waiting out a disconnect need this to stop
      // treating the session as at-risk: socket.js allows up to 120s per connect
      // attempt, so a caller timing the gap on its own would give up long before the
      // socket does. Fires before the join, so the caller can switch from "waiting for
      // transport" to "waiting for the join ack".
      handlersRef.current?.onConnected?.();
      // Once the caller has locked the session (idle timeout, or a connection
      // we know was dropped) rejoin is gated so we don't accidentally mint a
      // blank session. With hydration the server can restore from storage, so
      // the gate defers to the caller — pass through and let the server decide
      // whether the session is hydratable.
      const gate = handlersRef.current?.shouldRejoin;
      if (typeof gate === "function" && !gate()) return;
      emitJoin();
    };
    const onDisconnect = (reason) => {
      setIsConnected(false);
      handlersRef.current?.onDisconnected?.(reason);
    };

    sock.on("connect", onConnect);
    sock.on("disconnect", onDisconnect);

    const unbinders = EVENTS.map((evt) => {
      const key = toHandlerKey(evt);
      const fn = (payload) => {
        const h = handlersRef.current?.[key];
        if (typeof h === "function") h(payload);
      };
      sock.on(evt, fn);
      return () => sock.off(evt, fn);
    });

    if (sock.connected) onConnect();

    return () => {
      unbinders.forEach((off) => off());
      sock.off("connect", onConnect);
      sock.off("disconnect", onDisconnect);
      sock.disconnect();
      joinRef.current = null;
      socketRef.current = null;
      setSocketInstance(null);
      setIsConnected(false);
    };
    // Once per mount. The dashboard is remounted (keyed) when the engineer
    // opens a DIFFERENT session, which is the only time a fresh socket is
    // wanted; an id changing under the same mount — a blank chat being named,
    // a clear minting a successor — must keep the transport it has.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ask the server for a session id. The reply is tse:session_created, and
  // until it lands this client has no session — so every emitter below is a
  // no-op, which is why callers gate on adoptSession() rather than firing and
  // hoping. Deliberately NOT sent on mount: opening the copilot and walking
  // away should create nothing.
  const startSession = () => {
    const storedUsername = localStorage.getItem("username") || undefined;
    socketRef.current?.emit("tse:start", { username: storedUsername });
  };

  // Bind this hook to an id, synchronously. Called the instant a server-minted
  // id arrives, before anything is sent on it, so the emitters below target the
  // new session in the same tick instead of one render later.
  const adoptSession = (id) => {
    if (id) sessionIdRef.current = id;
  };

  // attachmentIds come from uploadAttachments() -- the server holds the bytes and
  // turns each id into text before the agent sees the turn.
  //
  // sessionIdRef.current, NOT the `sessionId` prop: the id is minted by the
  // SERVER now (tse:start -> tse:session_created), so on a blank chat the prop
  // is still null from the render that closed over it while the ref already
  // holds the real id. The guard is the same story -- emitting with no id
  // builds a server-side session nobody owns.
  // `answers`: batch card picks staged before the text was typed (F5-02). They go with the text as kind "text"
  // (tse_message.schema.json); the server applies them and reads the text for the rest. Without picks the payload is unchanged.
  const sendMessage = (msg, attachmentIds = [], answers = []) => {
    if (!sessionIdRef.current) return;
    const picks = Array.isArray(answers) && answers.length ? { kind: "text", answers } : {};
    socketRef.current?.emit("tse:message", {
      session_id: sessionIdRef.current,
      message: msg,
      attachment_ids: attachmentIds,
      ...picks,
    });
  };

  // params go nested — the server reads payload["params"].
  const sendAction = (action, params = {}) => {
    if (!sessionIdRef.current) return;
    socketRef.current?.emit("tse:action", { session_id: sessionIdRef.current, action, params });
  };

  // V5 panel / banner command (tse_action.schema.json, kind "command"): no LLM,
  // acked by tse:command_result, followed by a tse:diag_state when applied.
  const sendCommand = (action, params = {}) => {
    if (!sessionIdRef.current || !action) return false;
    socketRef.current?.emit("tse:action", { session_id: sessionIdRef.current, kind: "command", action, params });
    return true;
  };

  // V5 card answer (tse_message.schema.json, kind "answer"): answer_to is the
  // tse:question question_id, with option_id (single) or option_ids (multi).
  // `message` keeps the caption(s) for the chat bubble and the legacy path.
  // A DA batch card (tse:question `questions`) is answered once with `answers`
  // [{question_id, option_id}], one entry per question, and no option_id(s).
  const sendAnswer = ({ answerTo, optionId, optionIds, answers, message }) => {
    if (!sessionIdRef.current || !answerTo) return false;
    const picked = Array.isArray(answers) && answers.length
      ? { answers }
      : (Array.isArray(optionIds) && optionIds.length ? { option_ids: optionIds } : { option_id: optionId });
    socketRef.current?.emit("tse:message", {
      session_id: sessionIdRef.current,
      kind: "answer",
      answer_to: answerTo,
      ...picked,
      message,
      attachment_ids: [],
    });
    return true;
  };

  const stop = () => {
    if (!sessionIdRef.current) return;
    socketRef.current?.emit("tse:stop", { session_id: sessionIdRef.current });
  };

  const replaceSource = (turnId, sourceId, candidateId) => {
    if (!sessionIdRef.current || !turnId || !sourceId || !candidateId) return;
    socketRef.current?.emit("tse:replace_source", {
      session_id: sessionIdRef.current,
      turn_id: turnId,
      source_id: sourceId,
      candidate_id: candidateId,
    });
  };

  const citeSource = (turnId, sourceId) => {
    if (!sessionIdRef.current || !turnId || !sourceId) return;
    socketRef.current?.emit("tse:cite_source", {
      session_id: sessionIdRef.current,
      turn_id: turnId,
      source_id: sourceId,
    });
  };

  // Background sidebar search. Results arrive on tse:bugs / tse:cases and never
  // enter the chat transcript.
  const sendImplicitSearch = (type, topK = 5) => {
    if (!sessionIdRef.current) return;
    socketRef.current?.emit("tse:implicit_search", { session_id: sessionIdRef.current, type, top_k: topK });
  };

  // Tears the session down server-side. The server answers tse:cleared with a
  // successor id, which the caller adopts — on THIS socket, which is no longer
  // torn down and rebuilt to make the swap happen.
  // `message` is the typed "/clear" text, if any — the server stores it as the
  // retired session's closing turn so reopening it shows what ended it.
  const clearSession = (message = "") => {
    if (!sessionIdRef.current) return;
    socketRef.current?.emit("tse:clear", {
      session_id: sessionIdRef.current,
      ...(message ? { message } : {}),
    });
  };

  const exitGuided = () => {
    if (!sessionIdRef.current) return;
    socketRef.current?.emit("tse:exit_guided", { session_id: sessionIdRef.current });
  };

  // ASKS to re-run the first prompt under `product`. Fire-and-wait: the server
  // answers tse:product_change_accepted (room) or tse:product_change_refused
  // (this socket) — the caller wipes nothing until the first arrives.
  const changeProduct = (product) => {
    if (!sessionIdRef.current || !product) return;
    socketRef.current?.emit("tse:change_product", { session_id: sessionIdRef.current, product });
  };

  // Ends the session from the hybrid path. Hybrid answers come from a tool-less
  // single pass, so the model cannot close a session itself — it says so in its
  // text instead (APP_INTENT:CLOSE_SESSION), and this turns that checkpoint into
  // the same close the guided agent's tool performs, wrap-up and all.
  const closeSession = (summary = "") => {
    // sessionIdRef, not the `sessionId` argument — the same reason every other
    // emitter here reads the ref. The argument is a RENDER value, so it lags by
    // one render after an id is adopted, and both adoption paths send on the
    // spot: onSessionCreated runs its queued intents in the same tick as
    // adoptSession(), and onCleared emits straight after swapping in the
    // successor id. Closing through the argument in that window addresses a
    // session that is either null (a blank chat the server just named) or the
    // one that was retired a line earlier.
    if (!sessionIdRef.current) return;
    socketRef.current?.emit("tse:close_session", { session_id: sessionIdRef.current, summary });
  };

  /**
   * Renew the token and RECONNECT, after the server refused a join for want of an
   * identity (tse:access_denied with a retryable reason).
   *
   * Re-issuing join() cannot fix this: the server resolves the principal once, in
   * the connect handshake, and never re-evaluates it for the life of the socket —
   * so a socket that connected anonymously stays anonymous however many joins it
   * sends. Only a new connection re-runs socket.js's auth callback with the fresh
   * token, and `connect` then re-joins and replays the transcript by itself.
   *
   * Returns the discriminated refresh result so the caller can tell a dead session
   * (unauthorized) from a blip (network) — retrying the second, not the first.
   */
  const reauthenticate = async () => {
    const result = await refreshAccessTokenDetailed();
    // No new token means the reconnect would hand over the same rejected
    // credential; leave the socket alone and let the caller decide.
    if (!result?.token) return result || { error: "network" };
    const sock = socketRef.current;
    if (!sock) return result;
    sock.disconnect();
    sock.connect();
    return result;
  };

  // Re-issue the join for the live socket. Needed after the caller releases a
  // session lock: the refusal in `connect` leaves the socket connected but
  // unjoined, and `connect` will not fire a second time to correct it.
  const join = () => { joinRef.current?.(); };

  // Attaches the live call's context to the session. Not a question: the
  // agent records it and answers nothing (see tse:set_call_context).
  const setCallContext = (text) => {
    if (!sessionIdRef.current) return;
    socketRef.current?.emit("tse:set_call_context", { session_id: sessionIdRef.current, text });
  };

  // Claim the live-agent seat on ANOTHER session — the escalated customer's —
  // while this socket's own session stays the private copilot.
  //
  // Two ids on purpose. The engineer used to open the customer's id directly,
  // which made the copilot and the customer share one session and one room, so
  // every copilot prompt echoed into the customer's chat. Binding keeps them
  // apart: the customer's transcript is copied into the copilot session as
  // context, and nothing else crosses.
  const bindEscalation = (customerSessionId) => {
    if (!sessionIdRef.current || !customerSessionId) return;
    const stored = (localStorage.getItem("username") || "").trim();
    const lower = stored.toLowerCase();
    const agentName = stored && !["unknown", "user", "agent", "tse agent"].includes(lower)
      ? stored
      : undefined;
    socketRef.current?.emit("tse:bind_escalation", {
      copilot_session_id: sessionIdRef.current,
      customer_session_id: customerSessionId,
      ...(agentName ? { agent_name: agentName } : {}),
    });
  };

  const endLiveChat = (customerSessionId) => {
    if (!sessionIdRef.current || !customerSessionId) return;
    socketRef.current?.emit("tse:end_live_chat", {
      copilot_session_id: sessionIdRef.current,
      customer_session_id: customerSessionId,
    });
  };

  const syncAiAgentName = useCallback((name) => {
    const trimmed = String(name || "").trim();
    if (!trimmed || trimmed === "AI Agent") return;
    aiAgentNameRef.current = trimmed;
    localStorage.setItem("ArtaAiAgentName", trimmed);
    if (!sessionIdRef.current) return;
    socketRef.current?.emit("tse:set_ai_agent_name", {
      session_id: sessionIdRef.current,
      ai_agent_name: trimmed,
    });
  }, []);

  // Union of both branches: `closeSession` is the hybrid close path (the fast
  // stage has no tools, so the model reports the intent in its text and the
  // client fires the event), `startSession`/`adoptSession` are the server-mints-
  // the-id path. Dropping either silently disables a whole feature -- a missing
  // closeSession leaves closeSessionRef null and the close checkpoint does
  // nothing at all.
  return { sendMessage, sendAction, sendCommand, sendAnswer, replaceSource, citeSource, stop, sendImplicitSearch, clearSession, exitGuided, changeProduct, closeSession, join, reauthenticate, setCallContext, startSession, adoptSession, bindEscalation, endLiveChat, syncAiAgentName, isConnected, socket: socketInstance };
}
